import { useEffect, useMemo, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const safeString = (v) => (v === null || v === undefined ? '' : String(v));

const isMathOperator = (op) => ['+', '-', '*', '/'].includes(op);

const normalizeTokens = ({ tokens, categoriesById }) => {
  const arr = Array.isArray(tokens) ? tokens : [];
  return arr
    .map((t) => {
      if (!t || typeof t !== 'object') return null;
      const type = safeString(t.type).trim();
      if (type === 'category') {
        const categoryId = t.categoryId ?? t.category_id ?? null;
        if (!categoryId) return null;
        const categoryName = safeString(
          t.categoryName ?? t.category_name ?? categoriesById[String(categoryId)] ?? ''
        );
        return { type: 'category', categoryId, categoryName };
      }
      if (type === 'operator') {
        const value = safeString(t.value).trim();
        if (!value) return null;
        return { type: 'operator', value };
      }
      return null;
    })
    .filter(Boolean);
};

const tokenToText = (t) => {
  if (t?.type === 'operator') return t.value;
  if (t?.type === 'category') return t.categoryName || `Category_${t.categoryId}`;
  return '';
};

const getTokenKind = (t) => {
  if (!t || typeof t !== 'object') return 'unknown';
  if (t.type === 'category') return 'category';
  if (t.type === 'operator') {
    const v = safeString(t.value).trim();
    if (v === '(') return 'lparen';
    if (v === ')') return 'rparen';
    if (isMathOperator(v)) return 'op';
    return 'unknown';
  }
  return 'unknown';
};

const validateTokens = (tokens) => {
  const arr = Array.isArray(tokens) ? tokens : [];
  let balance = 0;
  let prev = 'start'; // start | value | op | lparen
  let hasCategory = false;

  for (const t of arr) {
    const kind = getTokenKind(t);

    if (kind === 'category') {
      hasCategory = true;
      if (prev === 'value') return { isValid: false, reason: 'Missing operator between categories.' };
      prev = 'value';
      continue;
    }

    if (kind === 'lparen') {
      // Allow: start, after operator, after '('
      if (prev === 'value') return { isValid: false, reason: 'Missing operator before "(".' };
      balance += 1;
      prev = 'lparen';
      continue;
    }

    if (kind === 'rparen') {
      // Allow only after a value
      if (prev !== 'value') return { isValid: false, reason: '")" must come after a value.' };
      balance -= 1;
      if (balance < 0) return { isValid: false, reason: 'Unmatched ")" in formula.' };
      prev = 'value';
      continue;
    }

    if (kind === 'op') {
      const op = safeString(t.value).trim();
      // Unary "-" is allowed at start / after operator / after "("
      if (op === '-' && (prev === 'start' || prev === 'op' || prev === 'lparen')) {
        prev = 'op';
        continue;
      }
      // Binary ops must come after a value
      if (prev !== 'value') return { isValid: false, reason: 'Operator must come after a value.' };
      prev = 'op';
      continue;
    }

    return { isValid: false, reason: 'Unknown token in formula.' };
  }

  if (!hasCategory) return { isValid: false, reason: 'Formula must include at least one category.' };
  if (balance !== 0) return { isValid: false, reason: 'Unclosed "(" in formula.' };
  if (prev === 'op' || prev === 'lparen') return { isValid: false, reason: 'Formula cannot end with an operator.' };

  return { isValid: true, reason: '' };
};

export const NetIncomeCreateEditSidebar = ({
  embedded = false,
  isOpen,
  onClose,
  mode = 'create', // create | edit
  calculation,
  categoriesToRender = [],
  onSave,
  isSaving = false,
  onRegisterSubmit,
  onCanSaveChange,
}) => {
  const initialSnapshotRef = useRef('');
  const submitRef = useRef(null);

  const categoriesById = useMemo(() => {
    const map = {};
    const rows = Array.isArray(categoriesToRender) ? categoriesToRender : [];
    rows.forEach((c) => {
      if (!c?.id) return;
      map[String(c.id)] = c?.name || '';
    });
    return map;
  }, [categoriesToRender]);

  const buildInitialState = useMemo(() => {
    if (mode === 'edit' && calculation) {
      const items = calculation?.items && typeof calculation.items === 'object' ? calculation.items : {};
      const tokens = normalizeTokens({ tokens: items?.tokens, categoriesById });
      return {
        name: safeString(calculation?.name || ''),
        tokens,
      };
    }
    return { name: '', tokens: [] };
  }, [mode, calculation, categoriesById]);

  const [localState, setLocalState] = useState(buildInitialState);

  useEffect(() => {
    if (!isOpen) return;
    setLocalState(buildInitialState);
    initialSnapshotRef.current = JSON.stringify(buildInitialState);
  }, [isOpen, buildInitialState]);

  const hasChanges = useMemo(() => {
    if (!isOpen) return false;
    const snap = initialSnapshotRef.current || '';
    return JSON.stringify(localState) !== snap;
  }, [isOpen, localState]);

  const validation = useMemo(() => {
    return validateTokens(localState?.tokens);
  }, [localState?.tokens]);

  const canSave = useMemo(() => {
    const nameOk = safeString(localState?.name).trim().length > 0;
    const tokensOk = Array.isArray(localState?.tokens) && localState.tokens.length > 0;
    return nameOk && tokensOk && validation.isValid && hasChanges && !isSaving;
  }, [localState?.name, localState?.tokens, validation.isValid, hasChanges, isSaving]);

  const lastTokenKind = useMemo(() => {
    const arr = Array.isArray(localState?.tokens) ? localState.tokens : [];
    return arr.length ? getTokenKind(arr[arr.length - 1]) : 'start';
  }, [localState?.tokens]);

  const parenBalance = useMemo(() => {
    const arr = Array.isArray(localState?.tokens) ? localState.tokens : [];
    let b = 0;
    for (const t of arr) {
      const kind = getTokenKind(t);
      if (kind === 'lparen') b += 1;
      if (kind === 'rparen') b -= 1;
    }
    return b;
  }, [localState?.tokens]);

  const addCategoryToken = (category) => {
    if (!category?.id) return;
    // Prevent category right after another category or right after ")"
    if (lastTokenKind === 'category' || lastTokenKind === 'rparen') return;
    setLocalState((prev) => ({
      ...prev,
      tokens: [
        ...(Array.isArray(prev.tokens) ? prev.tokens : []),
        { type: 'category', categoryId: category.id, categoryName: category.name || '' },
      ],
    }));
  };

  const addOperatorToken = (op) => {
    const value = safeString(op).trim();
    if (!value) return;
    // Prevent invalid sequences like two operators in a row (except unary "-"), invalid parens, etc.
    if (value === ')') {
      if (parenBalance <= 0) return;
      if (lastTokenKind !== 'category' && lastTokenKind !== 'rparen') return;
    } else if (value === '(') {
      if (lastTokenKind === 'category' || lastTokenKind === 'rparen') return;
    } else if (isMathOperator(value)) {
      // Unary "-" allowed at start / after operator / after "("
      if (value === '-' && (lastTokenKind === 'start' || lastTokenKind === 'op' || lastTokenKind === 'lparen')) {
        // ok
      } else {
        // other operators must follow a value
        if (lastTokenKind !== 'category' && lastTokenKind !== 'rparen') return;
      }
    } else {
      return;
    }
    setLocalState((prev) => ({
      ...prev,
      tokens: [...(Array.isArray(prev.tokens) ? prev.tokens : []), { type: 'operator', value }],
    }));
  };

  const deleteToken = (index) => {
    setLocalState((prev) => {
      const tokens = Array.isArray(prev.tokens) ? [...prev.tokens] : [];
      if (index < 0 || index >= tokens.length) return prev;
      tokens.splice(index, 1);
      return { ...prev, tokens };
    });
  };

  const clearTokens = () => {
    setLocalState((prev) => ({ ...prev, tokens: [] }));
  };

  const formulaText = useMemo(() => {
    const tokens = Array.isArray(localState?.tokens) ? localState.tokens : [];
    return tokens.map(tokenToText).filter(Boolean).join(' ');
  }, [localState?.tokens]);

  const handleSave = async () => {
    if (!canSave) return;
    const name = safeString(localState?.name).trim();
    const tokens = Array.isArray(localState?.tokens) ? localState.tokens : [];
    await onSave?.({ name, items: { tokens } });
  };

  useEffect(() => {
    submitRef.current = handleSave;
  }, [handleSave]);

  useEffect(() => {
    if (!onRegisterSubmit) return;
    onRegisterSubmit(() => submitRef.current?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRegisterSubmit]);

  useEffect(() => {
    onCanSaveChange?.(canSave);
  }, [canSave, onCanSaveChange]);

  const content = (
    <div className="px-0 py-4">
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Calculation Name</label>
        <input
          type="text"
          value={localState?.name || ''}
          onChange={(e) => setLocalState((p) => ({ ...p, name: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter calculation name"
          disabled={isSaving}
        />
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Formula</label>
        <div className="relative">
          <textarea
            value={formulaText}
            readOnly
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono min-h-[100px] resize-y bg-white"
            placeholder="Click categories and operators below to build your formula..."
          />
          {Array.isArray(localState?.tokens) && localState.tokens.length > 0 && (
            <button
              type="button"
              onClick={clearTokens}
              className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600"
              title="Clear formula"
              disabled={isSaving}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {Array.isArray(localState?.tokens) && localState.tokens.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {localState.tokens.map((t, index) => (
              <div
                key={`${t.type}-${t.categoryId ?? t.value}-${index}`}
                className="inline-flex items-center gap-1 bg-gray-100 px-2 py-1 rounded text-sm"
              >
                <span>{tokenToText(t)}</span>
                <button
                  type="button"
                  onClick={() => deleteToken(index)}
                  className="text-gray-500 hover:text-red-600"
                  title="Remove from formula"
                  disabled={isSaving}
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {!validation.isValid && Array.isArray(localState?.tokens) && localState.tokens.length > 0 && (
          <div className="mt-2 text-sm text-red-600">{validation.reason}</div>
        )}
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Categories</label>
        <div className="flex flex-wrap gap-2">
          {(Array.isArray(categoriesToRender) ? categoriesToRender : []).map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() => addCategoryToken(category)}
              className="px-3 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors text-sm font-medium"
              disabled={isSaving || lastTokenKind === 'category' || lastTokenKind === 'rparen'}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Operators</label>
        <div className="flex flex-wrap gap-2">
          {['+', '-', '*', '/', '(', ')'].map((op) => (
            <button
              key={op}
              type="button"
              onClick={() => addOperatorToken(op)}
              className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors text-lg font-semibold"
              disabled={
                isSaving ||
                (op === '(' && (lastTokenKind === 'category' || lastTokenKind === 'rparen')) ||
                (op === ')' &&
                  (parenBalance <= 0 || (lastTokenKind !== 'category' && lastTokenKind !== 'rparen'))) ||
                (['+', '*', '/'].includes(op) && (lastTokenKind !== 'category' && lastTokenKind !== 'rparen')) ||
                (op === '-' &&
                  !(
                    lastTokenKind === 'category' ||
                    lastTokenKind === 'rparen' ||
                    lastTokenKind === 'start' ||
                    lastTokenKind === 'op' ||
                    lastTokenKind === 'lparen'
                  ))
              }
            >
              {op}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  if (embedded) return content;

  return (
    // Keeping parity with TemplateCreateEditSidebar for non-embedded usage (not used by BudgetSettingsSidebar currently)
    <div>
      {content}
      <div className="flex justify-end gap-2 px-0 pb-4">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

